import { API_BASE } from '../../../lib/api-base';
import { useState } from 'react';
import { Modal } from '../../Modal';
import { useTranslation } from '../../../lib/i18n';
import type { WorkspaceApp } from '../../../lib/app-shares';
import { Button, Typography } from '../ui';
import styles from './DeleteAppDialog.module.css';

// Deliberately not a generic "Are you sure?". Three things are true at once and a user who confirms
// without knowing them has been misled:
//  - the app itself is destroyed, not just this card. It stops existing.
//  - everyone it was shared with loses access, silently, with no notification to them.
//  - none of it can be undone.
//
// The share COUNT is deliberately not shown. Reading it is a separate call that can fail, and a
// dialog that silently renders "nobody else is affected" when it simply could not find out has
// misled the user about the one fact most likely to change their mind. The sentence states that
// access is lost without claiming to know by how many people.
//
// So the dialog NAMES the app and says all three plainly. Typing the name is deliberately NOT
// required: the ceremony would suggest this is recoverable-if-careful, when what actually protects
// the user is knowing what the button does.
export function DeleteAppDialog({ app, onClose, onDeleted }: { app: WorkspaceApp; onClose: () => void; onDeleted: () => void }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const confirm = async () => {
    setBusy(true);
    setError(false);
    try {
      const res = await fetch(`${API_BASE}/api/delete-app`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId: app.id, workspaceId: app.workspaceId }),
      });
      if (!res.ok) {
        // A failed delete must say so and leave the card. Closing on failure would look like
        // success, and the app would still be there on the next load.
        setError(true);
        setBusy(false);
        return;
      }
      onDeleted();
    } catch {
      setError(true);
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      title={t('apps.delete.title', { name: app.name })}
      description={app.name}
      footer={
        <div className={styles.footer}>
          <Button variant="outlined" disabled={busy} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {/* Destructive, and it looks it: this button ends the app. */}
          <Button color="destructive" loading={busy} onClick={() => void confirm()}>
            {busy ? t('apps.delete.deleting') : t('apps.delete.confirm')}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <Typography variant="p2" color="secondary" component="p">
          {t('apps.delete.body')}
        </Typography>
        {error && (
          <Typography variant="p3" color="secondary" component="p">
            {t('apps.delete.failed')}
          </Typography>
        )}
      </div>
    </Modal>
  );
}
